import React, { useState, useEffect } from 'react';
import swal from 'sweetalert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faLock, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { Modal, Button } from 'react-bootstrap';
import axios from '../httpServices/httpService';
import api from '../config.json';

const EMPTY_FORM = { exist: '', new: '', confirmNew: '' };

const ChangePassword = ({ modelState, handleClose, user }) => {
  const [data, setData] = useState({ ...EMPTY_FORM });
  const [errors, setErrors] = useState({});
  const [visible, setVisible] = useState({ exist: false, new: false, confirmNew: false });
  const [saving, setSaving] = useState(false);

  // Start from a clean slate every time the dialog opens, so old values and
  // stale error messages are never shown again.
  useEffect(() => {
    if (modelState) {
      setData({ ...EMPTY_FORM });
      setErrors({});
      setVisible({ exist: false, new: false, confirmNew: false });
      setSaving(false);
    }
  }, [modelState]);

  const handleOnChange = (e) => {
    const { name, value } = e.currentTarget;
    setData((prev) => ({ ...prev, [name]: value }));
    // Clear the message for the field being edited, plus any server level error.
    setErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      delete next.form;
      return next;
    });
  };

  const toggleVisible = (field) => {
    setVisible((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const validate = () => {
    const found = {};
    const oldPwd = data.exist || '';
    const newPwd = data.new || '';
    const confirmPwd = data.confirmNew || '';

    if (!oldPwd.trim()) {
      found.exist = 'Old password is required';
    }

    if (!newPwd.trim()) {
      found.new = 'New password is required';
    } else if (oldPwd && newPwd === oldPwd) {
      found.new = 'New password must be different from the old password';
    }

    if (!confirmPwd.trim()) {
      found.confirmNew = 'Please confirm your new password';
    } else if (newPwd && confirmPwd !== newPwd) {
      found.confirmNew = 'Confirm password does not match the new password';
    }

    return found;
  };

  const handleSaveChange = () => {
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    const payload = { ...user };
    // The API expects the current password in `password` and the new one in `new`.
    payload.password = data.exist;
    payload.new = data.new;

    setSaving(true);
    axios
      .patch(api.dataEntryUrl + 'changepassword', payload)
      .then((result) => {
        setSaving(false);
        if (result.status === 200) {
          swal(`${result.data && result.data.message ? result.data.message : 'Password changed successfully'}`);
          handleClose();
        } else {
          setErrors({ form: result.statusText || 'Could not change the password. Please try again.' });
        }
      })
      .catch((err) => {
        setSaving(false);
        const serverMessage =
          (err.response && (err.response.data && (err.response.data.message || err.response.data))) ||
          err.message ||
          'Could not change the password. Please try again.';
        const text = String(serverMessage);

        // A wrong current password is the common failure, so point it at that field.
        if (/password/i.test(text) && /(invalid|incorrect|wrong|not match|mismatch)/i.test(text)) {
          setErrors({ exist: text });
        } else {
          setErrors({ form: text });
        }
      });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !saving) {
      handleSaveChange();
    }
  };

  const renderField = (name, label, placeholder, icon) => (
    <div className="form-group mb-3">
      <label htmlFor={`cp-${name}`} className="small font-weight-bold" style={{ color: '#334155' }}>
        {label}
      </label>
      <div className="input-group">
        <div className="input-group-prepend">
          <span className="input-group-text">
            <FontAwesomeIcon icon={icon} />
          </span>
        </div>
        <input
          id={`cp-${name}`}
          name={name}
          type={visible[name] ? 'text' : 'password'}
          className={errors[name] ? 'form-control is-invalid' : 'form-control'}
          value={data[name]}
          onChange={handleOnChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          // "new-password" on every field (including the old one) is what stops
          // Chrome/Edge from injecting the saved site password into these inputs.
          autoComplete="new-password"
          aria-label={label}
          aria-invalid={errors[name] ? 'true' : 'false'}
          aria-describedby={errors[name] ? `cp-${name}-error` : undefined}
        />
        <div className="input-group-append">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => toggleVisible(name)}
            aria-label={visible[name] ? `Hide ${label}` : `Show ${label}`}
            aria-pressed={visible[name]}
            title={visible[name] ? 'Hide password' : 'Show password'}
            tabIndex={-1}
          >
            <FontAwesomeIcon icon={visible[name] ? faEyeSlash : faEye} />
          </button>
        </div>
      </div>
      {errors[name] && (
        <div id={`cp-${name}-error`} className="text-danger small mt-1">
          {errors[name]}
        </div>
      )}
    </div>
  );

  return (
    <Modal show={modelState} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1.15rem', fontWeight: 700 }}>
          <FontAwesomeIcon icon={faLock} className="mr-2" />
          Change Password
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {errors.form && (
          <div className="alert alert-danger py-2 small mb-3" role="alert">
            {errors.form}
          </div>
        )}

        {renderField('exist', 'Old Password', 'Enter your current password', faKey)}
        {renderField('new', 'New Password', 'Enter your new password', faLock)}
        {renderField('confirmNew', 'Confirm New Password', 'Re-enter your new password', faLock)}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={saving}>
          Close
        </Button>
        <Button variant="primary" onClick={handleSaveChange} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ChangePassword;
